<template>
    <!-- 「고객 지원을 위한 접근 허용」 동의(계약 SUPPORT 2·9). mode=card: 직원 계정 화면 맨 위 카드, mode=choice: 첫 로그인 꼭 고르기 창만(앱 껍데기). 1년 재확인 없음(10-31) -->
    <template v-if="visible && consent">
        <div v-if="mode === 'card'" class="support-consent-card">
            <div class="support-consent-title">{{ card.title }}</div>
            <div class="support-consent-note support-consent-summary">{{ card.summary }}</div>
            <div class="support-consent-row">
                <span :class="['support-consent-dot', card.on ? 'on' : 'off']">{{ card.on ? '●' : '○' }}</span>
                <span class="support-consent-line">{{ card.text }}</span>
                <a-button v-if="card.action === 'ON'" size="small" type="primary" :loading="saving" @click="textOpen = true">허용하기</a-button>
                <a-button v-else size="small" :loading="saving" @click="askOff">허용 끄기</a-button>
            </div>
            <div v-if="card.byText" class="support-consent-note">{{ card.byText }}</div>
            <div v-if="card.offlineText" class="support-consent-note">{{ card.offlineText }}</div>
            <div class="support-consent-note">{{ SUPPORT_ACCOUNT_NOTE }}</div>
        </div>
        <a-modal v-model:open="textOpen" :title="card.title" :footer="null" centered destroyOnClose>
            <div class="support-consent-text">
                <template v-for="(line, i) in bodyLines" :key="i">
                    <div v-if="line.label" class="support-consent-item"><strong>{{ line.label }}</strong><span>{{ line.text }}</span></div>
                    <p v-else class="support-consent-intro">{{ line.text }}</p>
                </template>
            </div>
            <div class="support-consent-version">문구 판: {{ consent.text && consent.text.version }}</div>
            <div class="modal-footer-row">
                <a-button @click="textOpen = false">취소</a-button>
                <a-button type="primary" :loading="saving" @click="turnOn">동의하고 허용</a-button>
            </div>
        </a-modal>
        <!-- 첫 로그인 꼭 고르기 창: 닫기·ESC·바깥 클릭 없음, 고르면 서버가 needsChoice 를 끄고 창이 닫힘 -->
        <a-modal
            v-if="mode === 'choice'"
            :open="choiceOpen"
            :title="card.title"
            :footer="null"
            :closable="false"
            :keyboard="false"
            :maskClosable="false"
            centered
        >
            <div class="support-consent-text">
                <template v-for="(line, i) in bodyLines" :key="i">
                    <div v-if="line.label" class="support-consent-item"><strong>{{ line.label }}</strong><span>{{ line.text }}</span></div>
                    <p v-else class="support-consent-intro">{{ line.text }}</p>
                </template>
            </div>
            <div v-if="choiceError" class="support-consent-error" role="alert">{{ choiceError }}</div>
            <div class="modal-footer-row">
                <a-button :disabled="saving" @click="choose('OFF')">허용 안 함</a-button>
                <a-button type="primary" :loading="saving" @click="choose('ON')">허용</a-button>
            </div>
        </a-modal>
    </template>
</template>
<script>
import { message } from 'ant-design-vue';
import { accountSecurityApi } from '@/apis/accountSecurity';
import { alreadyNotified } from '@/utils/mfa';
import { getAccessToken, isSupportTab } from '@/utils/tokenStore';
import {
    CONSENT_OFF_CONFIRM_TEXT,
    CONSENT_OFF_DONE_TEXT,
    CONSENT_ON_DONE_TEXT,
    SUPPORT_ACCOUNT_NOTE,
    consentCardVisible,
    consentChoiceNeeded,
    consentErrorText,
    consentPutBody,
    consentView,
    consentBodyLines,
} from '@/utils/supportConsent';
import { twoBtnDialog } from '@/utils/utils';

export default {
    name: 'SupportConsentCard',
    props: {
        mode: { type: String, default: 'card' },
        // 부모가 이미 /auth/profile 을 읽었으면 넘김(없으면 여기서 읽음)
        companyManager: { type: Boolean, default: undefined },
    },
    data() {
        return { SUPPORT_ACCOUNT_NOTE, manager: this.companyManager, consent: null, saving: false, textOpen: false, choiceError: '' };
    },
    computed: {
        ctx() {
            // 동의 보기 권한(서버가 모드와 상관없이 늘 확인)이 없으면 부르지 않음 — 없는 직원은 403 알림(병원과 같음).
            // 권한 값(introspect)을 아직 못 읽었으면 null → 숨김, 오면 visible 감시가 읽음
            return { isCompanyManager: this.manager, supportTab: isSupportTab(), perms: this.$store.state.introspect?.profile?.perms ?? null };
        },
        visible() {
            return consentCardVisible(this.ctx);
        },
        card() {
            return consentView(this.consent);
        },
        bodyLines() {
            return consentBodyLines(this.consent?.text?.body);
        },
        choiceOpen() {
            return consentChoiceNeeded(this.consent, this.ctx);
        },
    },
    watch: {
        companyManager(v) {
            this.manager = v;
        },
        // 읽기는 여기 한 곳(관리자 표시·권한 값 중 늦게 온 쪽이 정함 — 사이드 메뉴가 권한 값을 따로 읽어 늦게 올 수 있음)
        visible: {
            handler(value) {
                if (value && !this.consent) this.load();
            },
            immediate: true,
        },
    },
    async mounted() {
        // 고르기 창은 로그인한 일반 세션만(로그인 전 화면에서 /auth/profile 을 부르지 않음)
        if (this.mode === 'choice' && !getAccessToken()) return;
        if (this.manager === undefined && !isSupportTab()) {
            try {
                this.manager = (await accountSecurityApi.getProfile())?.isCompanyManager === true;
            } catch {
                this.manager = false;
            }
        }
    },
    methods: {
        async load() {
            if (!this.visible) return;
            try {
                this.consent = (await this.$apiInstance.get('/support/consent')).data;
            } catch {
                // 서버가 아직 이 API 를 모르면(옛 inapi) 카드를 그리지 않음
                this.consent = null;
            }
        },
        async save(request, doneText) {
            this.saving = true;
            try {
                this.consent = (await request()).data;
                if (doneText) message.success(doneText);
                return true;
            } catch (error) {
                // 본인 확인 창을 닫았으면 실패가 아님. 공통 알림(axios)이 까닭을 띄운 코드는 짧은 문구만
                if (!alreadyNotified(error)) message.error({ content: consentErrorText(error), key: 'SUPPORT_CONSENT' });
                return false;
            } finally {
                this.saving = false;
            }
        },
        async turnOn() {
            // 2FA 켜진 세션이면 서버가 증표를 요구 → axios 의 step-up 창이 받아 한 번 다시 보냄
            const body = consentPutBody('ON', this.consent);
            if (!body) return; // 보여 준 문구의 판이 없으면 켜지 않음(서버 문구만 그림)
            const ok = await this.save(() => this.$apiInstance.put('/support/consent', body), CONSENT_ON_DONE_TEXT);
            if (ok) this.textOpen = false;
        },
        async askOff() {
            const yes = await twoBtnDialog('허용 끄기', CONSENT_OFF_CONFIRM_TEXT, '끄기', '취소');
            if (!yes) return;
            await this.save(() => this.$apiInstance.put('/support/consent', consentPutBody('OFF', this.consent)), CONSENT_OFF_DONE_TEXT);
        },
        // 고르기 창: 켜기는 turnOn 과 같은 요청(2FA 세션이면 axios step-up 이 증표 처리), 끄기는 확인 없이 바로. 실패는 창 안에
        async choose(status) {
            const body = consentPutBody(status, this.consent);
            if (!body) {
                this.choiceError = '동의 문구를 불러오지 못했습니다. 화면을 새로 고쳐 주세요.';
                return;
            }
            this.saving = true;
            this.choiceError = '';
            try {
                this.consent = (await this.$apiInstance.put('/support/consent', body)).data || this.consent;
            } catch (error) {
                // 인증번호 창을 닫았으면 실패 문구 없이 그대로(다시 고르면 됨)
                if (!error?.stepUpCancelled) this.choiceError = consentErrorText(error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
<style scoped>
.support-consent-card {
    border: 1px solid #e5e8eb;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    background: #fff;
}
.support-consent-title {
    font-weight: 700;
    margin-bottom: 8px;
}
.support-consent-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.support-consent-dot.on {
    color: #389e0d;
}
.support-consent-dot.off {
    color: #8c8c8c;
}
.support-consent-line {
    flex: 1;
    min-width: 200px;
}
.support-consent-note {
    margin-top: 6px;
    color: #595959;
    font-size: 13px;
}
.support-consent-summary {
    margin: 0 0 8px;
}
.support-consent-error {
    margin-top: 8px;
    color: #cf1322;
}
.support-consent-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 60vh;
    overflow: auto;
    line-height: 1.6;
    word-break: keep-all;
}
.support-consent-intro {
    margin: 0 0 4px;
    font-weight: 600;
}
.support-consent-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 12px;
    border-left: 3px solid #3292e3;
    border-radius: 4px;
    background: #eaf4fc;
}
.support-consent-item strong {
    color: #1f2937;
}
.support-consent-version {
    margin-top: 8px;
    color: #8c8c8c;
    font-size: 12px;
}
.modal-footer-row {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}
</style>
